/**
 * fleetProfilePhotoPull — FT-436 (2026-08-08). VESSELS-ONLY SLICE.
 *
 * Cross-device fix for the fleet-card PROFILE image. The fleet card
 * (FleetLanding) renders one card per VESSEL the user may see, each reading its
 * image from IDB `db.photos` at key `profile:<vesselId>` (useEntityProfilePhoto
 * → getFullUrl). But the vessel pull only pulls `sr.photos.v1` for the CURRENT
 * vessel — so on a FRESH device every OTHER vessel card had no photo in IDB and
 * showed "No photo yet", even though the cloud held it.
 *
 * This module is the pure, side-effect-controlled core of the fix, extracted
 * from UniversalSyncBridge so it is unit-testable without mounting the React /
 * VesselContext harness. It:
 *   1. builds the de-duplicated set of vessel ids, optionally skipping the id
 *      already covered by its own per-vessel pull;
 *   2. pulls `sr.photos.v1` per vessel via the injected cloud `pull`;
 *   3. keeps ONLY the `entity_profile` recordType (record-evidence photos ride
 *      their existing per-current-vessel pull — no fleet-wide bulk hydrate);
 *   4. dispatches the EXACT `sr:sync:hydrate` event the vessel photo path uses
 *      (module:'photos', vesselId:<vesselId> partition) so the shared
 *      `photoPullMerge` listener decodes base64 → writes IDB db.photos /
 *      db.photoThumbs → the card re-reads and displays it.
 *
 * ADDITIVE + read-only (never pushes): the crew-view-only gate is preserved (an
 * admin-set photo simply DISPLAYS for anyone who can see the vessel), and only
 * vessels in the caller's own server-scoped list are iterated (FT-435
 * assignment-scoping). NO facility path exists in this slice.
 *
 * It does NOT touch the UniversalSyncBridge LWW merge, the FT-453 justPulled
 * cooldown, or the FT-234 outbox — it rides the separate record-photo pipeline
 * (sr.photos.v1 → photoPullMerge) only.
 */

import { PROFILE_RECORD_TYPE } from '../components/common/EntityProfilePhoto/entityProfilePhoto';

/**
 * Build the ordered, de-duplicated list of vessel ids to pull profile photos
 * for. A vessel equal to `skipVesselId` (already covered by its own pull) is
 * excluded; every id is de-duplicated so a vessel id is never pulled twice.
 *
 * @param {Array<{id?: string}>|null|undefined} vessels
 * @param {{ skipVesselId?: string|null }} [opts]
 * @returns {string[]}
 */
export const fleetEntityIds = (vessels, opts = {}) => {
  const { skipVesselId = null } = opts;
  const ids = [];
  const seen = new Set();
  const consider = (id, skip) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    if (id === skip) return; // covered by that vessel's own pull
    ids.push(id);
  };
  for (const v of (Array.isArray(vessels) ? vessels : [])) consider(v && v.id, skipVesselId);
  return ids;
};

/**
 * Pull profile photos for every fleet VESSEL and dispatch a photo-hydrate event
 * per vessel. Reuses the existing `sr.photos.v1` round-trip end-to-end.
 *
 * @param {Object} args
 * @param {(collection: string, entityId: string, since: number) => Promise<any>} args.pull
 *   the cloud client's pull (returns an array of records, or `{ error, status }`).
 * @param {Array<{id?: string}>} args.vessels     server-scoped vessel list.
 * @param {string|null} [args.skipVesselId]        current vessel (own pull covers it).
 * @param {(evt: CustomEvent) => void} [args.dispatch]  event sink (defaults to
 *   window.dispatchEvent) — injectable for tests / non-browser envs.
 * @param {(msg: string, err?: any) => void} [args.onError]  optional error log.
 * @returns {Promise<{ entities: number, dispatched: number }>} summary.
 */
export const pullFleetProfilePhotos = async ({
  pull,
  vessels,
  skipVesselId = null,
  dispatch,
  onError,
}) => {
  const summary = { entities: 0, dispatched: 0 };
  if (typeof pull !== 'function') return summary;

  const emit = typeof dispatch === 'function'
    ? dispatch
    : (evt) => { try { window.dispatchEvent(evt); } catch (_) { /* non-browser */ } };

  const ids = fleetEntityIds(vessels, { skipVesselId });
  summary.entities = ids.length;

  for (const entityId of ids) {
    try {
      const result = await pull('sr.photos.v1', entityId, 0);
      // FT-49 error signal (403/network): never hydrate on error.
      if (result && result.error) {
        if (onError) onError(`fleet profile-photo pull skipped for ${entityId} (status: ${result.status})`);
        continue;
      }
      const docs = Array.isArray(result) ? result : [];
      // Unwrap the { data } envelope each record carries → flat photo records.
      const records = docs.map((d) => (d && d.data !== undefined ? d.data : d));
      // Keep ONLY profile photos — record-evidence photos are pulled by their
      // own per-current-vessel path. photoPullMerge accepts a pre-filtered array.
      const profileRecords = records.filter(
        (r) => r && r.recordType === PROFILE_RECORD_TYPE
      );
      if (profileRecords.length === 0) continue;

      emit(new CustomEvent('sr:sync:hydrate', {
        detail: {
          module: 'photos',      // photoPullMerge listens for module === 'photos'
          vesselId: entityId,    // vessel partition = this vessel id
          value: profileRecords,
          source: 'cloud',
          timestamp: Date.now(),
        },
      }));
      summary.dispatched += 1;
    } catch (err) {
      if (onError) onError(`fleet profile-photo pull failed for ${entityId}`, err);
    }
  }
  return summary;
};

export default pullFleetProfilePhotos;
